/**
 * The ONE scenery rule for generated backgrounds, about ONE game. A game draws
 * its own objects over its background, so a background must never show one
 * of them (or anything a player could mistake for one), nor any interface.
 * Whether it may show ground, floors, walkways or other structures depends on
 * the game: a game whose bodies stand on, walk along, climb or land on
 * surfaces it draws itself (a platformer, a mine climber, a lander) must not
 * get painted look-alike surfaces — they show routes that do not exist
 * (2026-10-17: the accepted 1983-mario background painted pipe rows and a
 * ladder, the bounty-bob one a lattice of catwalks; 2026-10-19: the
 * Tranquility Nine background painted lit landing pads). A game where
 * nothing stands on the background (a tap-to-serve counter, a shooter, a
 * top-down flight) is free to dress its scene: the genre-blind rule rejected
 * Scoop Rush's beach boardwalk twice and then a distant shark fin, and the
 * game failed for want of a background (2026-10-19).
 *
 * The rule is DERIVED, never assumed: {@link SceneryRuleV1} comes from the
 * game itself — a composed game's archetypes and modules (each module
 * declares its `surfaceContact`), a maintained profile's declared
 * presentation, a free-form design's entities. Every party renders it from
 * here: the background generation prompt ({@link backgroundSceneryClauseV1},
 * its surface contact only — never the object list an image model would paint),
 * the background judge's core `sceneryOnly` criterion
 * ({@link backgroundSceneryCriterionV1}), the composed synthesis prompt that
 * authors a composed game's background line ({@link backgroundLineRuleV1})
 * and the deterministic check `deriveComposedGameV1` applies to that line
 * ({@link backgroundLineProblemsV1}) — so the line the model writes, the
 * check that admits it, the image the provider draws and the verdict that
 * accepts it all state the same game's rule.
 */
import type { GameDesign } from '../design/types.js';

/** Each structure as the prose names it and the word forms that name it. */
const STRUCTURES: ReadonlyArray<{ name: string; forms: string }> = [
  { name: 'platforms', forms: 'platforms?' },
  { name: 'ledges', forms: 'ledges?' },
  { name: 'girders', forms: 'girders?' },
  { name: 'beams', forms: 'beams?' },
  { name: 'catwalks', forms: 'catwalks?' },
  { name: 'scaffolding', forms: 'scaffold(?:s|ing|ings)?' },
  { name: 'gantries', forms: 'gantry|gantries' },
  { name: 'ladders', forms: 'ladders?' },
  { name: 'stairs', forms: 'stairs?|staircases?|stairways?' },
  { name: 'ramps', forms: 'ramps?' },
  { name: 'bridges', forms: 'bridges?' },
  { name: 'walkways', forms: 'walkways?' },
  { name: 'pipes', forms: 'pipes?|pipework|piping' },
  { name: 'rails', forms: 'rails?|railings?' },
  { name: 'pads', forms: 'pads?|helipads?|launchpads?' },
];

/** The playable structures as one prose list, for a surface-contact game's
 * prompts and judge rubric. */
export const PLAYABLE_STRUCTURES = `${STRUCTURES.slice(0, -1).map(s => s.name).join(', ')} or ${STRUCTURES.at(-1)!.name}`;

const STRUCTURE_MATCHERS = STRUCTURES.map(s => ({ name: s.name, re: new RegExp(`\\b(?:${s.forms})\\b`, 'i') }));

/** What one game's background must leave to the game. */
export interface SceneryRuleV1 {
  /** The game's own objects, each drawn by the game over the background: its
   * kind (entity) name and subject line. */
  entities: ReadonlyArray<{ name: string; role: string }>;
  /** A body in this game stands on, walks along, climbs or lands on surfaces
   * the game draws itself, so the background paints no playable structure. */
  surfaceContact: boolean;
}

/** The rule of a game presented as render kinds (a composition's
 * presentation: kind → role, plus its derived or declared surface contact). */
export function presentationSceneryRuleV1(presentation: {
  entities: Readonly<Record<string, { role: string }>>;
  surfaceContact: boolean;
}): SceneryRuleV1 {
  return {
    entities: Object.entries(presentation.entities).map(([name, entity]) => ({ name, role: entity.role })),
    surfaceContact: presentation.surfaceContact,
  };
}

/** The rule of an art-plan design: its entities, and the surface contact a
 * composition declares on its scenery. A free-form design (or none) does not
 * declare which surfaces its bodies use, so it keeps every playable structure
 * out of its background. */
export function designSceneryRuleV1(design: GameDesign | undefined): SceneryRuleV1 {
  return {
    entities: (design?.entities ?? []).map(entity => ({ name: entity.name, role: entity.role })),
    surfaceContact: design?.scenery?.surfaceContact !== false,
  };
}

const spoken = (name: string): string => name.replace(/[-_]+/g, ' ').trim();

/** The game's objects as one prose list (`name (role); …`), or null. */
function objectList(rule: SceneryRuleV1): string | null {
  return rule.entities.length ? rule.entities.map(e => `${spoken(e.name)} (${e.role})`).join('; ') : null;
}

/** The background generation prompt's scenery clause. It never lists the
 * game's objects: an image model paints what a prompt names, negated or not
 * (a real gemini run given the lander's object list drew the descent module,
 * a landing pad and a debris shard, each captioned, 2026-10-19). The objects
 * are the judge's to check ({@link backgroundSceneryCriterionV1}); the
 * generator gets the game's surface contact. */
export function backgroundSceneryClauseV1(rule: SceneryRuleV1): string {
  return 'Scenery only: no characters, creatures, vehicles, items or other game objects, and no UI, HUD, ' +
    'gauges, buttons or text; the game draws its own objects over this background. ' +
    (rule.surfaceContact
      ? `The game's characters stand on, walk along, climb or land on surfaces the game draws itself, so paint ` +
        `no ${PLAYABLE_STRUCTURES} and nothing else that suggests a surface to stand on, walk along, climb or land on: ` +
        'no ground band, floor or terrain seen from the side in the near or middle distance — at most a thin, ' +
        'faint, distant horizon.'
      : 'Nothing in this game stands, walks or lands on the background, so ground, floors, counters, ' +
        'boardwalks, roads and buildings are welcome set dressing.');
}

/** The background judge's core `sceneryOnly` criterion for this game. */
export function backgroundSceneryCriterionV1(rule: SceneryRuleV1): string {
  const objects = objectList(rule);
  return 'the scene is scenery behind THIS game, which draws its own objects over it' +
    `${objects ? `: ${objects}` : ''}. Fail when the image clearly shows one of the game's objects or something ` +
    'a player could mistake for one, or any UI, HUD, gauge, button or text panel. ' +
    (rule.surfaceContact
      ? `This game's characters stand on, walk along, climb or land on surfaces the game draws itself, so also ` +
        `fail on any painted ${PLAYABLE_STRUCTURES}, any ground band, floor or terrain surface seen from the side ` +
        'in the near or middle distance, or anything else a character could appear to stand on, walk along, climb ' +
        'or land on, even faintly. Pass only terrain seen from directly above (a top-down map of fields, roads, ' +
        'rivers), walls, and a thin, faint, distant horizon, rock or skyline. '
      : 'Nothing in this game stands, walks or lands on the background, so ground, floors, counters, boardwalks, ' +
        'piers, roads, bridges, buildings and other set dressing pass unless they are one of the game\'s objects. ') +
    'Distant or incidental living things that are none of the game\'s objects (far-off birds, a distant fin or ' +
    'sail) are scenery and pass unless a player could mistake them for one of its objects.';
}

/** The head noun of a kind name (`beach-customer` → `customer`). */
const headNoun = (name: string): string => spoken(name).split(/\s+/).at(-1)!.toLowerCase();
const escape = (word: string): string => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** The game's object head nouns a background line must not name (a head a
 * structure already names is left to that structure). */
function objectHeads(rule: SceneryRuleV1): string[] {
  const structures = rule.surfaceContact ? STRUCTURE_MATCHERS : [];
  return [...new Set(rule.entities.map(e => headNoun(e.name)).filter(Boolean))]
    .filter(head => !structures.some(m => m.re.test(head)));
}

/** The words a background line must not name: the game's object head nouns,
 * and — for a surface-contact game — the playable structures. */
function forbiddenWords(rule: SceneryRuleV1): Array<{ name: string; re: RegExp }> {
  return [
    ...objectHeads(rule).map(head => ({ name: head, re: new RegExp(`\\b${escape(head)}(?:s|es)?\\b`, 'i') })),
    ...(rule.surfaceContact ? STRUCTURE_MATCHERS : []),
  ];
}

/** How a background art line is written for this game: the instruction the
 * line's author reads, naming exactly what {@link backgroundLineProblemsV1}
 * rejects. */
export function backgroundLineRuleV1(rule: SceneryRuleV1): string {
  const heads = objectHeads(rule);
  return 'describe only the scenery behind play (setting, sky, landscape, light), never one of the game\'s own ' +
    `objects${heads.length ? ` (${heads.join(', ')})` : ''}, people or creatures up close, or any interface` +
    (rule.surfaceContact
      ? `; its characters stand on, walk along, climb or land on surfaces the game draws itself, so never ${PLAYABLE_STRUCTURES}, ` +
        'nor anything else a character could stand on, walk along, climb or land on'
      : '');
}

/** What a background art line names that this game's background must not
 * show (its objects' head nouns, then — for a surface-contact game — the
 * playable structures, in list order); empty when the line is scenery only. */
export function backgroundLineProblemsV1(line: string, rule: SceneryRuleV1): string[] {
  return forbiddenWords(rule).filter(m => m.re.test(line)).map(m => m.name);
}
